import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useMarkDocketHasPrinted } from "../../../hooks/api/mutations/useSection";
import { useGetSectionItemDetails } from "../../../hooks/api/queries/useSection";
import { cleanObject } from "../../../helpers";
import { useCloseOtpAuthorizationPrompt } from "../../../hooks/useCloseOtpAuthorizationPrompt";
import usePrintLogic from "../usePrintLogic";

const useSectionView = ({
  section_id,
  sectionPrintComponent,
  hiddenId,
  isDocket = false,
  closePrint = () => {},
  invalidateQueries = () => {},
}) => {
  // Mobile devices can't reach a local print-agent server (127.0.0.1),
  // so they fall back to opening a generated PDF instead — see MobilePrint.
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const { closeOtpAuthorizationPrompt } = useCloseOtpAuthorizationPrompt();
  const { business_name, address } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );

  const { data: sectionDetails, refetch: refetchSectionItems } =
    useGetSectionItemDetails(
      cleanObject({
        id: section_id,
        enabled: false,
        onSuccess: () => {
          closeOtpAuthorizationPrompt();
        },
      }),
    );

  const { handleMarkDocketHasPrinted } = useMarkDocketHasPrinted({
    id: section_id,
    callBackFunc: () => {
      invalidateQueries();
      closeOtpAuthorizationPrompt();
      closePrint();
    },
  });

  const clearAfterPrint = () => {
    if (isDocket && !sectionDetails?.is_printed)
      return handleMarkDocketHasPrinted();
    closeOtpAuthorizationPrompt();
    closePrint();
  };

  const { paperSize, receipt_footer_title, receipt_footer_description } =
    usePrintLogic({
      // Only feeds the direct-print pipeline on desktop — on mobile,
      // MobilePrint handles printing on its own once sectionDetails loads.
      itemDetails: !isMobile ? sectionDetails : null,
      sectionPrintComponent,
      hiddenId,
      callBackFunc: clearAfterPrint,
      onErrorCallBackFunc: closePrint,
    });

  useEffect(() => {
    refetchSectionItems();
  }, []);

  return {
    isMobile,
    business_name,
    address,
    sectionDetails,
    paperSize,
    receipt_footer_title,
    receipt_footer_description,
    clearAfterPrint,
  };
};

export default useSectionView;
